import {alpha} from "@mui/material/styles";

// ----------------------------------------------------------------------

// SETUP COLORS
const GREY = {
    "0": "#FFFFFF",
    "100": "#F5F8FA",
    "200": "#A4A9AD",
    "300": "#D1DBE3",
    "400": "#e1e5e9",
    "500": "#90A1AD",
    "600": "#6A7D8A",
    "700": "#4A5A66",
    "800": "#36434D",
    "900": "#6b6d6f",
    "1000": "#171D21",
    "A700": "#6b6d6f1a",
};

const PRIMARY = {
    "contrastText": "#fff",
    "dark": "#005172",
    "darkText": "#0078A7",
    "darker": "#005172",
    "light": "#3095B4",
    "lightText": "#025172",
    "lighter": "#D1E9FC",
    "main": "#0078a7",
    "grayPrimary": "#161919"
};

const SECONDARY = {
    "contrastText": "#fff",
    "dark": "#fff",
    "darker": "#091A7A",
    "light": "#6b6d6f1a",
    "lighter": "#D6E4FF",
    "main": GREY[900],
};


const FONTSTYPO = {
    "stepMinues2": "clamp(0.64rem, calc(0.62rem + 0.11vw), 0.80rem)",
    "stepMinues1": "clamp(0.80rem, calc(0.77rem + 0.14vw), 1.00rem)",
    "step0": "clamp(1.00rem, calc(0.96rem + 0.18vw), 1.25rem)",
    "step1": "clamp(1.25rem, calc(1.20rem + 0.22vw), 1.56rem)",
    "step2": "clamp(1.56rem, calc(1.51rem + 0.28vw), 1.95rem)",
    "step3": "clamp(1.95rem, calc(1.78rem + 0.86vw), 2.44rem)",
    "step4": "clamp(2.44rem, calc(2.35rem + 0.44vw), 3.05rem)"
};


const SPACING = {
    "space3xs": "clamp(0.25rem, calc(0.24rem + 0.04vw), 0.31rem)",
    "space2xs": "clamp(0.50rem, calc(0.48rem + 0.09vw), 0.63rem)",
    "spacexs": "clamp(0.75rem, calc(0.72rem + 0.13vw), 0.94rem)",
    "spaces": "clamp(1.00rem, calc(0.96rem + 0.18vw), 1.25rem)",
    "spacem": "clamp(1.50rem, calc(1.44rem + 0.27vw), 1.88rem)",
    "spacel": "clamp(2.00rem, calc(1.93rem + 0.36vw), 2.50rem)",
    "spacexl": "clamp(3.00rem, calc(2.89rem + 0.54vw), 3.75rem)",
    "space2xl": "clamp(4.00rem, calc(3.85rem + 0.72vw), 5.00rem)",
    "space3xl": "clamp(6.00rem, calc(5.78rem + 1.08vw), 7.50rem)",
    "space3xs2xs": "clamp(0.25rem, calc(0.19rem + 0.27vw), 0.63rem)",
    "space2xsxs": "clamp(0.50rem, calc(0.44rem + 0.31vw), 0.94rem)",
    "spacexss": "clamp(0.75rem, calc(0.68rem + 0.36vw), 1.25rem)",
    "spacesM": "clamp(1.00rem, calc(0.87rem + 0.63vw), 1.88rem)",
    "spacemL": "clamp(1.50rem, calc(1.35rem + 0.72vw), 2.50rem)",
    "spacelXl": "clamp(2.00rem, calc(1.74rem + 1.25vw), 3.75rem)",
    "spacexl2xl": "clamp(3.00rem, calc(2.71rem + 1.43vw), 5.00rem)",
    "space2xl3xl": "clamp(4.00rem, calc(3.49rem + 2.51vw), 7.50rem)",
    "spacesL": "clamp(1.00rem, calc(0.78rem + 1.08vw), 2.50rem)",
    "spacesXl": "clamp(1.00rem, calc(0.60rem + 1.97vw), 3.75rem)",
};

const COMMON = {
    "borderRadius": FONTSTYPO.step0,
    "shadow1": "0 1px 10px rgba(0,120,167,0.12), 0 1px 8px rgba(0,120,167,0.24)",
    "grid": {
        "--space" : SPACING.spacexss,
        "display": "grid",
        "gap": "var(--space , clamp(0.75rem, calc(0.57rem + 0.88vw), 1.25rem))",
    }
};


const palette = {
    "action": {
        "active": GREY[600],
        "disabled": alpha(GREY[500], 0.8),
        "disabledBackground": alpha(GREY[500], 0.24),
        "disabledOpacity": 0.48,
        "focus": alpha(GREY[500], 0.24),
        "hover": alpha(GREY[500], 0.08),
        "hoverOpacity": 0.08,
        "selected": alpha(GREY[500], 0.16),
    },
    "background": {
        "default": "#FFF",
        "paper": "#F6F7F8",
    },
    "common": {"black": "#000", "white": "#fff"},
    "divider": alpha(GREY[500], 0.24),
    "grey": GREY,
    "primary": PRIMARY,
    "secondary": SECONDARY,
    "text": {
        "disabled": GREY[500],
        "primary": "#101F28",
        "secondary": PRIMARY.darker,
    },
};

const BluePalette = {
    "action": {
        "active": GREY[600],
        "disabled": alpha(GREY[500], 0.8),
        "disabledBackground": alpha(GREY[500], 0.24),
        "disabledOpacity": 0.48,
        "focus": alpha(GREY[500], 0.24),
        "hover": alpha(GREY[500], 0.08),
        "hoverOpacity": 0.08,
        "selected": alpha(GREY[500], 0.16),
    },
    "background": {
        "default":PRIMARY.darker,
        "neutral": "#F6F7F8",
        "paper": PRIMARY.darker,
    },
    "common": {"black": "#fff", "white": "#000"},
    "divider": alpha(GREY[500], 0.24),
    "grey": GREY,
    "primary": {
        "contrastText": "#fff",
        "dark": "#fff",
        "darkText": "#0078A7",
        "darker": "#fff",
        "light": "#3095B4",
        "lightText": "#025172",
        "lighter": "#D1E9FC",
        "main": "#fff"
    },
    "secondary": {
        "contrastText": "#fff",
        "dark": "#005172",
        "darker": GREY[900],
        "light": "#ddd",
        "lighter": "#D6E4FF",
        "main":"#000",
    },
    "text": {
        "disabled": GREY[500],
        "primary": "#101F28",
        "secondary": PRIMARY.darker,
    },
};


const HeadingStyles  = {
    "h1": {
        "fontSize": FONTSTYPO.step4,
        "fontWeight": 350,
        "lineHeight": 1.2,
    },
    "h2": {
        "fontSize": FONTSTYPO.step3,
        "fontWeight": 350,
        "lineHeight":1.2,
    },
    "h3": {
        "fontSize":FONTSTYPO.step2,
        "fontWeight": 350,
        "lineHeight": 1.3,
    },
    "h4": {
        "fontSize": FONTSTYPO.step1,
        "fontWeight": 350,
        "lineHeight": 1.3,
    },
    "h5": {
        "fontSize": FONTSTYPO.step0,
        "fontWeight": 350,
        "lineHeight": 1.33,
    },
    "h6": {
        "fontSize": FONTSTYPO.step0,
        "fontWeight": 350,
        "lineHeight": 1.3,
    },
};


export {
    BluePalette,
    COMMON,
    FONTSTYPO,
    HeadingStyles,
    palette,
    PRIMARY,
    SPACING};
